import { useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { needsReportPrompt, type SavedReport } from './report-questions';

/**
 * Whether to offer the wizard right now, asked at the moment an experiment is ended or a
 * winner is deployed.
 *
 * A function rather than a query so the details screen does not fetch a report on every
 * page view just to answer a question that only matters once, at End or Deploy. It reads the same
 * cache entry as useExperimentReport, always revalidated, since a report may have been
 * written on the tab or by someone else since the page loaded.
 *
 * If the report cannot be read it says yes: the prompt is optional and skippable, and a
 * missed prompt is worse than a redundant one.
 */
export const useReportPrompt = (experimentId: number) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();

  return async () => {
    try {
      const report = await queryClient.fetchQuery<SavedReport | null>({
        queryKey: ['EXPERIMENT_REPORT', experimentId],
        queryFn: () => apiClient.get<SavedReport | null>(`/experiments/${experimentId}/report`),
        staleTime: 0,
      });
      return needsReportPrompt(report);
    } catch {
      return true;
    }
  };
};
